import { SourcingChannels } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Check as CheckIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import PlusIcon from '@/assets/icons/plus-sm.svg';
import { buildChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { INDEED } from '@/types/channels';
import { cn } from '@/utils/classnames';

import { CardLayout } from './channel-card/card-layout';
import { DirectChannelCard } from './channel-card/direct-channel-card';
import { VonqChannelCard } from './channel-card/vonq-channel-card';
import { useChannelDescription } from './use-channel-description';
import { areAllOpeningsUnsupported } from './use-unsupported-openings';

interface SectionsProps {
  title: string;
  description: string;
  channels: SourcingChannels['popular_channels'];
  sectionKey: 'popular' | 'free' | 'paid';
  isIndeedIntegrationEnabled: boolean;
  forceExpanded?: boolean;
}

export function Sections({
  title,
  description,
  channels,
  sectionKey,
  isIndeedIntegrationEnabled,
  forceExpanded = false,
}: SectionsProps) {
  const isFree = sectionKey === 'free';
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { getValues, setValue } = hookForm;
  const { getTitle } = useChannelDescription();

  const sectionRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(sectionKey === 'popular');
  const effectivelyExpanded = forceExpanded || isExpanded;

  const totalFreeChannels = sectionKey === 'free' ? channels.count : 0;
  const formChannels = useWatch({
    control: hookForm.control,
    name: 'channels',
  });
  const selectedFreeChannels = Object.values(formChannels ?? {}).filter(
    channel => channel.isFree,
  );

  /**
   * Add all free channels to the job posting.
   * If the channel is not publishable, skip it.
   * If all openings are unsupported by a channel, skip it.
   * If the channel is already selected, skip it.
   * Build the channel defaults depening of weather its Pool or direct or Vonq and add it to the job posting.
   */
  const handleAddAllFreeChannels = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const existingChannels = getValues('channels') ?? {};
    const merged = { ...existingChannels };

    for (const channel of channels.direct_channels) {
      if (!channel.is_publishable) continue;
      if (
        areAllOpeningsUnsupported(
          channel.unsupported_openings,
          selectedOpeningIds,
        )
      )
        continue;

      if (merged[channel.slug]) continue;

      merged[channel.slug] = buildChannelDefaults({
        channelSlug: channel.slug,
        channelName: channel.name,
        displayTitle: getTitle(channel.slug, channel.name),
        openingIds: selectedOpeningIds,
        unsupportedOpenings: channel.unsupported_openings ?? [],
        isFree: true,
      });
    }

    for (const channel of channels.vonq_channels) {
      if (
        areAllOpeningsUnsupported(
          channel.unsupported_openings,
          selectedOpeningIds,
        )
      )
        continue;

      if (Object.values(merged).some(ch => ch.slug === channel.channel.slug))
        continue;

      // Get the openings selected for another product of the same channel
      const openingsSelectedForAnotherProduct = Object.entries(merged)
        .filter(
          ([key, ch]) =>
            key !== channel.product_id && ch.slug === channel.channel.slug,
        )
        .flatMap(([, ch]) => Object.keys(ch.openingBudgets));

      merged[channel.product_id] = buildChannelDefaults({
        channelSlug: channel.channel.slug,
        channelName: channel.channel.name,
        displayTitle: channel.title ?? channel.channel.name,
        openingIds: selectedOpeningIds,
        unsupportedOpenings: channel.unsupported_openings ?? [],
        excludeOpenings: openingsSelectedForAnotherProduct,
        isFree: true,
        vonqDetails: {
          productId: channel.product_id,
          contractId: channel.contract_id,
          title: channel.title,
          channelId: channel.channel.id,
          channelName: channel.channel.name,
          logoSquareUrl: channel.channel.logo_square_url,
          logoRectangleUrl: channel.channel.logo_rectangle_url,
          budget: channel.vonq_price[0]?.amount ?? 0,
          duration: channel.duration.period,
          channelType: channel.channel.type,
          processingTime: channel.time_to_process.period,
        },
      });
    }

    setValue('channels', merged);
  };

  const handleSectionToggle = () => {
    if (sectionKey === 'popular' || forceExpanded) return;
    const willExpand = !isExpanded;
    setIsExpanded(prev => !prev);

    if (willExpand && sectionRef.current) {
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  };

  return (
    <>
      <div ref={sectionRef} className="flex scroll-mt-[200px] flex-col gap-4">
        <button
          type="button"
          onClick={handleSectionToggle}
          className={cn(
            'hover:bg-fx-neutral-50 flex items-center justify-between gap-2 text-left',
            sectionKey !== 'popular' && 'rounded-lg p-4',
          )}
        >
          {/* Title and description */}
          <div className="flex flex-col items-start gap-1">
            <Typography variant="medium2">{title}</Typography>
            <Typography variant="body1" className="text-fx-neutral-600">
              {description}
            </Typography>
          </div>

          {/* Add all button for free section */}
          {sectionKey === 'free' && (
            <Button
              type="button"
              variant={selectedFreeChannels.length > 0 ? 'brand' : 'secondary'}
              onClick={handleAddAllFreeChannels}
            >
              {selectedFreeChannels.length > 0 ? (
                <>
                  <CheckIcon className="size-4 text-white" />
                  <FormattedMessage
                    defaultMessage="Added ({count}/{total})"
                    description="Button to indicate that the channels have been added to the job posting"
                    values={{
                      count: selectedFreeChannels.length,
                      total: totalFreeChannels,
                    }}
                  />
                </>
              ) : (
                <>
                  <img src={PlusIcon} alt="" />
                  <FormattedMessage
                    defaultMessage="Add all"
                    description="Button to add all channels to the job posting"
                  />
                </>
              )}
            </Button>
          )}

          {/* Expand/collapse button */}
          {sectionKey !== 'popular' && !forceExpanded && (
            <>
              {effectivelyExpanded ? (
                <ChevronUp className="text-fx-neutral-600 size-4" />
              ) : (
                <ChevronDown className="text-fx-neutral-600 size-4" />
              )}
            </>
          )}
        </button>

        {effectivelyExpanded && (
          <>
            {/* Direct channels */}
            {channels.direct_channels.map(channel => (
              <DirectChannelCard
                key={channel.slug}
                channel={channel}
                isIndeedIntegrationDisabled={
                  channel.slug === INDEED && !isIndeedIntegrationEnabled
                }
                isFree={sectionKey === 'free'}
              />
            ))}

            {/* Vonq channels */}
            {channels.vonq_channels.map(channel => (
              <VonqChannelCard
                key={channel.product_id}
                channel={channel}
                isFree={isFree}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
}

Sections.Skeleton = function SectionsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-25" />
          <Skeleton className="mt-1 h-4 w-100" />
        </div>
      </div>

      {_range(6).map(index => (
        <CardLayout.Skeleton key={index} />
      ))}
    </div>
  );
};
